import { useCurrentAccount, useSuiClient } from '@mysten/dapp-kit';
import {
	BRIDGE_CHAIN_ID_BNB,
	fetchBridgeLockedEvents,
	mistToDgc,
	type BridgeLockedEventRow,
} from '@cpc/tx';
import { useQuery } from '@tanstack/react-query';
import { Heading } from '@mysten/ui';
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { PageLayout } from '~/components/Layout/PageLayout';
import { CpcPageHero, cpcPageContentClass } from '~/components/cpc/CpcPageHero';
import { useT } from '~/i18n/LocaleContext';
import { Card } from '~/ui/Card';
import { cpcAmountToUsdt, getBridgeCpcUsdtRate, getBridgeMarketConfig, usdtAmountToCpc } from '~/config/bridgeConfig';
import { fetchPushHubBridgeHistory, type PushHubBridgeEvent } from '~/hooks/pushHubClient';
import { tryFormatCpcAddress, tryNormalizeToHexAddress } from '~/utils/cpcAddress';

const USDT_SYMBOL = 'BNB USDT';
const MUTED = 'text-[#AEB9D0]';
const DIM = 'text-[#94a3b8]';

type HistoryRow = {
	id: string;
	direction: 'data-to-usdt' | 'usdt-to-data';
	timestampMs: number | null;
	amountLabel: string;
	estimateLabel: string;
	counterpart: string;
	href: string;
	external?: boolean;
};

function formatLockTime(ms: number | null): string {
	if (!ms) return '—';
	try {
		return new Date(ms).toLocaleString();
	} catch {
		return '—';
	}
}

function formatWeiUsdt(wei: string): string {
	try {
		const raw = BigInt(wei || '0');
		const base = 10n ** 18n;
		const whole = raw / base;
		const frac = (raw % base).toString().padStart(18, '0').replace(/0+$/, '');
		if (!frac) return whole.toString();
		return `${whole}.${frac.slice(0, 8)}`;
	} catch {
		return wei || '0';
	}
}

function hubEventToRow(row: PushHubBridgeEvent, rate: number): HistoryRow {
	const inbound = row.direction === 'usdt-to-data';
	const dataAmt = row.amount_mist ? mistToDgc(row.amount_mist) : '';
	const usdtAmt = row.amount_wei ? formatWeiUsdt(row.amount_wei) : '';
	if (inbound) {
		const usdt = usdtAmt || dataAmt;
		return {
			id: row.id || `in-${row.nonce}`,
			direction: 'usdt-to-data',
			timestampMs: row.timestamp_ms || null,
			amountLabel: `${usdt} USDT`,
			estimateLabel: `${dataAmt || usdtAmountToCpc(usdt, rate) || usdt} DATA`,
			counterpart: row.counterparty,
			href: row.bsc_tx ? `https://bscscan.com/tx/${row.bsc_tx}` : '/bridge',
			external: !!row.bsc_tx,
		};
	}
	const data = dataAmt || usdtAmt;
	return {
		id: row.id || `out-${row.nonce}`,
		direction: 'data-to-usdt',
		timestampMs: row.timestamp_ms || null,
		amountLabel: `${data} DATA`,
		estimateLabel: `${usdtAmt || cpcAmountToUsdt(data, rate)} ${USDT_SYMBOL}`,
		counterpart: row.counterparty,
		href: row.dgc_digest ? `/txblock/${row.dgc_digest}` : '/bridge',
	};
}

export default function CpcBridgeHistoryPage() {
	const t = useT();
	const [params] = useSearchParams();
	const client = useSuiClient();
	const cpcAccount = useCurrentAccount();
	const config = getBridgeMarketConfig();
	const rate = getBridgeCpcUsdtRate();

	const address = (params.get('address') || cpcAccount?.address || '').trim();
	const queryAddress = tryNormalizeToHexAddress(address) || address;
	const displayAddress = address ? tryFormatCpcAddress(address) ?? address : '';

	const hubQ = useQuery({
		queryKey: ['cpc-bridge-history', 'hub', queryAddress],
		enabled: !!queryAddress,
		queryFn: () => fetchPushHubBridgeHistory(queryAddress, 50),
		staleTime: 5_000,
		refetchOnMount: 'always',
		refetchOnWindowFocus: true,
		refetchInterval: 8_000,
	});

	const chainFallbackQ = useQuery({
		queryKey: ['cpc-bridge-history', 'out', config?.packageId, queryAddress],
		enabled: !!config && !!queryAddress && !hubQ.isLoading && (hubQ.isError || (hubQ.data?.length ?? 0) === 0),
		queryFn: async () => {
			if (!config || !queryAddress) return [];
			return fetchBridgeLockedEvents(client, config.packageId, {
				chainId: BRIDGE_CHAIN_ID_BNB,
				sender: queryAddress,
				limit: 50,
			});
		},
		staleTime: 8_000,
	});

	const rows = useMemo((): HistoryRow[] => {
		if ((hubQ.data?.length ?? 0) > 0) {
			return hubQ.data!.map((row) => hubEventToRow(row, rate));
		}
		return (chainFallbackQ.data ?? []).map((row: BridgeLockedEventRow) => ({
			id: `out-${row.digest}-${row.nonce}`,
			direction: 'data-to-usdt' as const,
			timestampMs: row.timestampMs,
			amountLabel: `${row.amountCpc} DATA`,
			estimateLabel: `${cpcAmountToUsdt(row.amountCpc, rate)} ${USDT_SYMBOL}`,
			counterpart: row.recipientHex,
			href: `/txblock/${row.digest}`,
		}));
	}, [hubQ.data, chainFallbackQ.data, rate]);

	const isFetching = hubQ.isFetching || chainFallbackQ.isFetching;
	const isLoading = hubQ.isLoading || chainFallbackQ.isLoading;

	if (!config) {
		return (
			<PageLayout
				content={
					<div className="mx-auto max-w-2xl py-8 text-center text-white">
						<Heading variant="heading4/semibold" color="white" className="mb-2 text-2xl font-bold">
							{t('bridge.historyTitle')}
						</Heading>
						<p className={`mt-2 ${DIM}`}>{t('bridge.noConfig')}</p>
						<Link to="/bridge" className="mt-6 inline-flex text-sm font-medium text-[#38BDF8]">
							{t('bridge.historyBackToBridge')}
						</Link>
					</div>
				}
			/>
		);
	}

	return (
		<PageLayout
			gradient={{
				size: 'md',
				content: (
					<CpcPageHero
						title={t('bridge.historyTitle')}
						subtitle={t('bridge.historySubtitle')}
						actions={
							<Link
								to="/bridge"
								className="inline-flex h-11 items-center rounded-xl bg-white/5 px-4 text-sm font-semibold text-[#AEB9D0] hover:text-white"
							>
								{t('bridge.historyBackToBridge')}
							</Link>
						}
					/>
				),
			}}
			content={
				<div className={cpcPageContentClass}>
					<Card bg="dark" spacing="lg">
						<div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
							<div className="flex items-center gap-2">
								<h2 className="text-lg font-semibold text-white">{t('bridge.onChainLocksAccount')}</h2>
								{address ? (
									<span className={`inline-flex items-center gap-1.5 text-xs ${MUTED}`}>
										<span
											className={`h-1.5 w-1.5 rounded-full bg-[#38BDF8] ${
												isFetching ? 'animate-pulse' : ''
											}`}
										/>
										{t('bridge.historyAutoRefresh')}
									</span>
								) : null}
							</div>
							{displayAddress ? (
								<span className={`max-w-full truncate font-mono text-xs ${DIM}`} title={address}>
									{t('bridge.historyAddress')}: {displayAddress}
								</span>
							) : null}
						</div>

						{!queryAddress ? (
							<p className={`py-8 text-center text-sm ${MUTED}`}>{t('bridge.historyNeedWallet')}</p>
						) : isLoading ? (
							<div className="flex justify-center py-10">
								<svg
									className="h-6 w-6 animate-spin text-[#38BDF8]"
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
								>
									<circle
										className="opacity-25"
										cx="12"
										cy="12"
										r="10"
										stroke="currentColor"
										strokeWidth="4"
									/>
									<path
										className="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
									/>
								</svg>
							</div>
						) : rows.length === 0 ? (
							<p className={`py-8 text-center text-sm ${MUTED}`}>{t('bridge.noOnChainLockEvents')}</p>
						) : (
							<div className="overflow-x-auto">
								<table className={`w-full text-[11px] sm:text-xs ${MUTED}`}>
									<thead>
										<tr className={`border-b border-white/10 text-left ${DIM}`}>
											<th className="pb-2 pr-2 font-medium">{t('bridge.colTime')}</th>
											<th className="pb-2 pr-2 font-medium">{t('bridge.colDirection')}</th>
											<th className="pb-2 pr-2 font-medium">{t('bridge.colAmount')}</th>
											<th className="hidden pb-2 pr-2 font-medium sm:table-cell">
												{t('bridge.colEstimate')}
											</th>
											<th className="hidden pb-2 pr-2 font-medium sm:table-cell">
												{t('bridge.colRecipient')}
											</th>
											<th className="pb-2 text-right font-medium">{t('bridge.colAction')}</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-white/10">
										{rows.map((row) => (
											<tr key={row.id} className="hover:bg-white/5">
												<td className={`py-2.5 pr-2 ${DIM}`}>{formatLockTime(row.timestampMs)}</td>
												<td className="py-2.5 pr-2">
													<span
														className={
															row.direction === 'data-to-usdt'
																? 'text-[#FE2C55]'
																: 'text-[#38BDF8]'
														}
													>
														{row.direction === 'data-to-usdt'
															? t('bridge.dirDataToUsdt')
															: t('bridge.dirUsdtToData')}
													</span>
												</td>
												<td className="py-2.5 pr-2 font-medium tabular-nums text-white">
													{row.amountLabel}
												</td>
												<td className="hidden py-2.5 pr-2 tabular-nums text-[#38BDF8] sm:table-cell">
													{row.estimateLabel}
												</td>
												<td
													className={`hidden max-w-[120px] truncate py-2.5 pr-2 font-mono sm:table-cell ${DIM}`}
													title={row.counterpart}
												>
													{row.counterpart}
												</td>
												<td className="py-2.5 text-right">
													{row.external ? (
														<a
															href={row.href}
															target="_blank"
															rel="noreferrer"
															className="font-medium text-[#38BDF8] hover:text-sky-300"
														>
															{t('bridge.details')}
														</a>
													) : (
														<Link
															to={row.href}
															className="font-medium text-[#38BDF8] hover:text-sky-300"
														>
															{t('bridge.details')}
														</Link>
													)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</Card>
				</div>
			}
		/>
	);
}
